import React, { useState, useRef } from 'react';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

interface ReceiptUploadProps {
    bookingId: string;
    existingReceipts?: string[];
    onReceiptsChange: (receipts: string[]) => void;
    disabled?: boolean;
    maxFiles?: number;
    label?: string;
    helperText?: string;
}

interface UploadedFile {
    url: string;
    name: string;
    type: string;
}

const ReceiptUpload: React.FC<ReceiptUploadProps> = ({
    bookingId,
    existingReceipts = [],
    onReceiptsChange,
    disabled = false,
    maxFiles = 5,
    label = "Upload Receipts",
    helperText = "Upload images or PDF files of receipts (max 10MB each)"
}) => {
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>(() => {
        return existingReceipts.map(url => ({
            url,
            name: getFileNameFromUrl(url),
            type: getFileTypeFromUrl(url)
        }));
    });
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    function getFileNameFromUrl(url: string): string {
        try {
            const urlParts = url.split('/');
            const fileName = urlParts[urlParts.length - 1];
            return decodeURIComponent(fileName.split('?')[0]);
        } catch {
            return 'Receipt';
        }
    }

    function getFileTypeFromUrl(url: string): string {
        const fileName = getFileNameFromUrl(url);
        const extension = fileName.split('.').pop()?.toLowerCase();
        return extension === 'pdf' ? 'application/pdf' : 'image';
    }

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        setError(null);
        setUploading(true);

        try {
            const storage = getStorage();
            const newFiles: UploadedFile[] = [];

            for (let i = 0; i < files.length; i++) {
                const file = files[i];

                // Validate file type
                if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
                    setError('Only image files and PDFs are allowed');
                    continue;
                }

                // Validate file size (10MB)
                if (file.size > 10 * 1024 * 1024) {
                    setError('File size must be less than 10MB');
                    continue;
                }

                // Check if we've reached the max file limit
                if (uploadedFiles.length + newFiles.length >= maxFiles) {
                    setError(`Maximum ${maxFiles} files allowed`);
                    break;
                }

                // Upload to Firebase Storage
                const timestamp = Date.now();
                const fileName = `${timestamp}_${file.name}`;
                const storageRef = ref(storage, `receipts/${bookingId}/${fileName}`);

                await uploadBytes(storageRef, file);
                const downloadURL = await getDownloadURL(storageRef);

                newFiles.push({
                    url: downloadURL,
                    name: file.name,
                    type: file.type
                });
            }

            const updatedFiles = [...uploadedFiles, ...newFiles];
            setUploadedFiles(updatedFiles);
            onReceiptsChange(updatedFiles.map(f => f.url));

        } catch (error) {
            console.error('Error uploading files:', error);
            setError('Failed to upload files. Please try again.');
        } finally {
            setUploading(false);
            // Reset the input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleRemoveFile = async (fileToRemove: UploadedFile) => {
        try {
            // Try to delete from storage (may fail if file doesn't exist, that's ok)
            try {
                const storage = getStorage();
                const fileName = getFileNameFromUrl(fileToRemove.url);
                const storageRef = ref(storage, `receipts/${bookingId}/${fileName}`);
                await deleteObject(storageRef);
            } catch (deleteError) {
                console.warn('Could not delete file from storage:', deleteError);
            }

            const updatedFiles = uploadedFiles.filter(f => f.url !== fileToRemove.url);
            setUploadedFiles(updatedFiles);
            onReceiptsChange(updatedFiles.map(f => f.url));
        } catch (error) {
            console.error('Error removing file:', error);
            setError('Failed to remove file');
        }
    };

    const LoadingSpinner = () => (
        <div style={{
            width: '20px',
            height: '20px',
            border: '2px solid rgba(192, 192, 221, 0.3)',
            borderTop: '2px solid #C0C0DD',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginRight: '8px'
        }} />
    );

    const FileIcon = ({ type }: { type: string }) => {
        if (type === 'application/pdf') {
            return (
                <div style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: '#E0B1F1',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold'
                }}>
                    PDF
                </div>
            );
        }
        return (
            <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: '#C0C0DD',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold'
            }}>
                IMG
            </div>
        );
    };

    return (
        <div style={{ marginTop: '16px' }}>
            <style jsx>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .upload-button {
                    display: inline-flex;
                    align-items: center;
                    padding: 8px 16px;
                    margin-bottom: 16px;
                    border: 1px solid #C0C0DD;
                    background-color: transparent;
                    color: #C0C0DD;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 500;
                    transition: all 0.2s ease;
                }
                
                .upload-button:hover:not(:disabled) {
                    border-color: #E0B1F1;
                    background-color: rgba(224, 177, 241, 0.1);
                    color: #E0B1F1;
                }
                
                .upload-button:disabled {
                    border-color: rgba(192, 192, 221, 0.3);
                    color: rgba(192, 192, 221, 0.3);
                    cursor: not-allowed;
                }
                
                .hidden-input {
                    position: absolute;
                    width: 1px;
                    height: 1px;
                    padding: 0;
                    margin: -1px;
                    overflow: hidden;
                    clip: rect(0, 0, 0, 0);
                    white-space: nowrap;
                    border: 0;
                }
                
                .alert {
                    padding: 12px 16px;
                    margin-bottom: 16px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    font-size: 14px;
                }
                
                .alert-info {
                    background-color: rgba(103, 58, 183, 0.1);
                    border: 1px solid rgba(103, 58, 183, 0.3);
                    color: white;
                }
                
                .alert-error {
                    background-color: rgba(211, 47, 47, 0.1);
                    border: 1px solid rgba(211, 47, 47, 0.3);
                    color: white;
                }
                
                .alert-close {
                    background: none;
                    border: none;
                    color: inherit;
                    cursor: pointer;
                    margin-left: auto;
                    padding: 4px;
                    font-size: 18px;
                }
                
                .file-list {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                }
                
                .file-item {
                    background-color: rgba(255, 255, 255, 0.05);
                    margin-bottom: 8px;
                    border-radius: 4px;
                    border: 1px solid rgba(224, 177, 241, 0.2);
                    padding: 12px;
                    display: flex;
                    align-items: center;
                    transition: all 0.2s ease;
                }
                
                .file-item:hover {
                    background-color: rgba(255, 255, 255, 0.08);
                    border-color: rgba(224, 177, 241, 0.4);
                }
                
                .file-info {
                    flex: 1;
                    margin-left: 12px;
                }
                
                .file-name {
                    color: white;
                    font-weight: 500;
                    margin: 0 0 4px 0;
                    font-size: 14px;
                }
                
                .file-chip {
                    display: inline-block;
                    padding: 2px 8px;
                    border: 1px solid #C0C0DD;
                    color: #C0C0DD;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 500;
                }
                
                .delete-button {
                    background: none;
                    border: none;
                    color: rgba(255, 255, 255, 0.7);
                    cursor: pointer;
                    padding: 4px;
                    border-radius: 4px;
                    transition: all 0.2s ease;
                    font-size: 18px;
                }
                
                .delete-button:hover:not(:disabled) {
                    color: #f44336;
                    background-color: rgba(244, 67, 54, 0.1);
                }
                
                .delete-button:disabled {
                    color: rgba(255, 255, 255, 0.3);
                    cursor: not-allowed;
                }
            `}</style>

            {label && (
                <h3 style={{
                    color: '#C0C0DD',
                    fontWeight: 600,
                    margin: '0 0 8px 0',
                    fontSize: '16px'
                }}>
                    {label}
                </h3>
            )}

            {helperText && (
                <p style={{
                    margin: '0 0 16px 0',
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '14px'
                }}>
                    {helperText}
                </p>
            )}

            {/* Upload Button */}
            <button
                className="upload-button"
                disabled={disabled || uploading || uploadedFiles.length >= maxFiles}
                onClick={() => fileInputRef.current?.click()}
            >
                {uploading ? (
                    <>
                        <LoadingSpinner />
                        Uploading...
                    </>
                ) : (
                    <>
                        <span style={{ marginRight: '8px', fontSize: '16px' }}>☁</span>
                        Choose Files
                    </>
                )}
            </button>

            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf"
                onChange={handleFileSelect}
                className="hidden-input"
            />

            {uploadedFiles.length >= maxFiles && (
                <div className="alert alert-info">
                    <span>ℹ</span>
                    <span style={{ marginLeft: '8px' }}>
                        Maximum number of files ({maxFiles}) reached
                    </span>
                </div>
            )}

            {error && (
                <div className="alert alert-error">
                    <span>⚠</span>
                    <span style={{ marginLeft: '8px' }}>{error}</span>
                    <button
                        className="alert-close"
                        onClick={() => setError(null)}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>
            )}

            {/* Uploaded Files List */}
            {uploadedFiles.length > 0 && (
                <div>
                    <h4 style={{
                        color: '#C0C0DD',
                        fontWeight: 600,
                        margin: '0 0 12px 0',
                        fontSize: '14px'
                    }}>
                        Uploaded Receipts ({uploadedFiles.length})
                    </h4>
                    <ul className="file-list">
                        {uploadedFiles.map((file, index) => (
                            <li key={index} className="file-item">
                                <FileIcon type={file.type} />
                                <div className="file-info">
                                    <p className="file-name">{file.name}</p>
                                    <span className="file-chip">
                                        {file.type === 'application/pdf' ? 'PDF' : 'Image'}
                                    </span>
                                </div>
                                <button
                                    className="delete-button"
                                    onClick={() => handleRemoveFile(file)}
                                    disabled={disabled}
                                    aria-label="Delete file"
                                >
                                    🗑
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default ReceiptUpload; 